import {
  Badge,
  Button,
  Card,
  CycleView,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  ObservationCellIcons,
  ObservationLegend,
  PhaseBandStack,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  describeDayPhases,
  getObservationCellStyle,
  getSymptomHighlightClass,
} from "@nfp-tracker/components";
import {
  cn,
  generatePDF,
  getCurrentUserTimeZone,
  getCyclePhases,
  getDateKeyInTimeZone,
  formatTemperature,
  isPostPeakPhaseDay,
  normalizeTimeZone,
  type NFPMethod,
  type PhaseSpan,
} from "@nfp-tracker/utils";
import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfMonth,
  format,
  isSameDay,
  parseISO,
  startOfMonth,
  subMonths,
} from "date-fns";
import { ChevronLeft, ChevronRight, FileText } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SubscriptionGate } from "../components/SubscriptionGate";
import { useToast } from "@nfp-tracker/components";
import { CALENDAR_FULLSCREEN_EVENT } from "../constants/events";
import { ROUTES } from "../constants/routes";
import { useCalendarViewPreferences } from "../hooks/useCalendarViewPreferences";
import { useObservationsCache } from "../hooks/useObservationsCache";
import { useOwnerContext } from "../hooks/useOwnerContext";
import { captureException } from "../integrations/analytics/client";
import { fetchAllObservations } from "../utils/observationsCsvIo";
import {
  deriveCyclesFromObservations,
  getCycleForDate,
  getCycleTiming,
  isPastCycle,
  type DerivedObservation,
} from "../utils/derivedCycles";

const Calendar = () => {
  const [viewMode, setViewMode] = useState<"month" | "cycle">("cycle");
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [activeSymptomFilters, setActiveSymptomFilters] = useState<string[]>([]);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const { visibleIconTypes, visiblePhases, toggleIconType, togglePhase } =
    useCalendarViewPreferences();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Shared, session-cached auth/owner/permission context — repeat navigation between pages
  // no longer re-runs the auth+onboarding+owner-resolution chain from scratch each time.
  const ownerContextQuery = useOwnerContext();
  const ownerContext = ownerContextQuery.data;
  const couple = ownerContext?.status === "ready" ? ownerContext.couple : null;
  const coupleId = couple?.id;
  // Memoized because the fallback is a fresh array literal, which would otherwise re-run the
  // phase-span memo on every render.
  const methods = useMemo(
    () => (couple?.methods?.length ? couple.methods : ["Marquette"]),
    [couple?.methods]
  );
  const isPostpartum = ownerContext?.status === "ready" ? ownerContext.isPostpartum : false;
  // This page is reused inside instructor mode to render a client's chart. There the entry
  // affordances must not just be disabled but absent: they navigate to the *instructor's own*
  // Observations page, which would silently drop them out of the client's chart.
  const isClientView =
    ownerContext?.status === "ready" && Boolean(ownerContext.viewingClientUserId);
  const coupleTimezone = couple ? normalizeTimeZone(couple.timezone) : getCurrentUserTimeZone();
  const userName = couple ? `${couple.first_name || ""} ${couple.last_name || ""}`.trim() : "";

  // Shared cache (~13 months / ~12 cycles), kept fresh by the single realtime subscription
  // mounted in AppShellLayout — no per-page fetch or realtime subscription needed here.
  const { cycles, observations: cycleViewObservations, isLoading: loadingObservations } =
    useObservationsCache(coupleId, methods);
  const loading = ownerContextQuery.isLoading || loadingObservations;

  useEffect(() => {
    if (ownerContext?.status === "unauthenticated") {
      navigate(ROUTES.auth);
    } else if (ownerContext?.status === "needs-onboarding") {
      navigate(ROUTES.onboarding);
    }
  }, [ownerContext, navigate]);

  useEffect(() => {
    if (ownerContextQuery.error) {
      console.error(ownerContextQuery.error);
      captureException(ownerContextQuery.error, { source: "calendar_owner_context" });
    }
  }, [ownerContextQuery.error]);

  const observations = useMemo(() => {
    const monthStart = format(startOfMonth(currentMonth), "yyyy-MM-dd");
    const monthEnd = format(endOfMonth(currentMonth), "yyyy-MM-dd");
    return cycleViewObservations.filter(
      (obs) => obs.observation_date >= monthStart && obs.observation_date <= monthEnd
    );
  }, [cycleViewObservations, currentMonth]);

  // Phase spans are computed once per cycle here rather than inside the grids: the fertile
  // window rules need the selected methods, the postpartum flag, and the shortest cycle on
  // record — none of which a single cycle row can see.
  const phasesByCycleId = useMemo(() => {
    const observationsByCycleId = new Map<string, DerivedObservation[]>();
    for (const obs of cycleViewObservations) {
      const forCycle = observationsByCycleId.get(obs.cycle_id) ?? [];
      forCycle.push(obs);
      observationsByCycleId.set(obs.cycle_id, forCycle);
    }

    // Only cycles that ended before today have an observed length (a pre-logged future start
    // gives the current cycle an end_date it hasn't reached), and partial cycles (start never
    // recorded) have no real length at all — neither may feed the shortest − 20 rule.
    const today = getDateKeyInTimeZone(new Date(), coupleTimezone);
    const completedLengths = cycles
      .filter((cycle) => isPastCycle(cycle, today) && !cycle.start_inferred)
      .map(
        (cycle) =>
          differenceInCalendarDays(
            parseISO(cycle.end_date as string),
            parseISO(cycle.start_date)
          ) + 1
      );
    const shortestCycleLength = completedLengths.length
      ? Math.min(...completedLengths)
      : null;

    return new Map<string, { spans: PhaseSpan[]; likelyNewCycleCd: number | null }>(
      cycles.map((cycle) => [
        cycle.id,
        getCyclePhases(cycle, observationsByCycleId.get(cycle.id) ?? [], {
          methods: methods as NFPMethod[],
          shortestCycleLength,
          completedCycleCount: completedLengths.length,
          isPostpartum,
          today,
        }),
      ])
    );
  }, [cycles, cycleViewObservations, methods, isPostpartum, coupleTimezone]);
  const phaseSpansByCycleId = useMemo(
    () => new Map([...phasesByCycleId].map(([id, phases]) => [id, phases.spans])),
    [phasesByCycleId]
  );

  useEffect(() => {
    const openFullscreen = () => setIsFullscreenOpen(true);
    window.addEventListener(CALENDAR_FULLSCREEN_EVENT, openFullscreen);
    return () => window.removeEventListener(CALENDAR_FULLSCREEN_EVENT, openFullscreen);
  }, []);

  const handlePrevious = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const handleNext = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const toggleSymptomFilter = (symptom: string) => {
    setActiveSymptomFilters((current) =>
      current.includes(symptom)
        ? current.filter((s) => s !== symptom)
        : [...current, symptom]
    );
  };

  const getObservationForDay = (day: Date) => {
    return observations.find((obs) => isSameDay(parseISO(obs.date), day));
  };

  // Only a recorded start gets the CD1 badge — a partial cycle's first day is just the
  // earliest entry, not the first day of a cycle.
  const getCycleForDay = (day: Date) => {
    return cycles.find(
      (cycle) => !cycle.start_inferred && isSameDay(parseISO(cycle.start_date), day)
    );
  };

  const handleExportPDF = async () => {
    try {
      if (!coupleId) return;

      // Paged: a single request is capped at 1000 rows, which would silently drop history.
      const allObservations = await fetchAllObservations(coupleId);

      const { cycles: allCycles, observations: normalizedObs } =
        deriveCyclesFromObservations(allObservations, methods);

      generatePDF(normalizedObs || [], allCycles || [], userName, methods, coupleTimezone);

      toast({
        title: "Export Complete",
        description: "Your PDF report has been generated.",
      });
    } catch (error: unknown) {
      toast({
        title: "Export Error",
        description: (error as Error).message,
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-24 w-full" />
        <Card className="p-6">
          <div className="grid grid-cols-7 gap-2">
            {Array.from({ length: 35 }).map((_, i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        </Card>
      </div>
    );
  }

  const todayKey = getDateKeyInTimeZone(new Date(), coupleTimezone);
  const cycleObservationsByDate = new Map(
    cycleViewObservations.map((obs) => [obs.date, obs])
  );
  const selectedObs = selectedDay
    ? cycleObservationsByDate.get(format(selectedDay, "yyyy-MM-dd")) || null
    : null;
  const monthDays = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth),
  });
  const monthStartOffset = monthDays[0]?.getDay() || 0;
  // Future-dated cycles (pre-logged entries) are data too, so they're shown — labelled
  // "Upcoming" — rather than hidden; "Current" is the cycle containing today, not the newest.
  const upcomingCount = cycles.filter((cycle) => getCycleTiming(cycle, todayKey) === "upcoming")
    .length;
  // Most recent cycle whose post-Peak phase was cut short by what looks like the next period
  // (an unmarked cycle start) — prompt to mark it rather than guessing a start ourselves.
  const unmarkedStart = cycles
    .filter((cycle) => getCycleTiming(cycle, todayKey) !== "upcoming")
    .map((cycle) => ({ cycle, cd: phasesByCycleId.get(cycle.id)?.likelyNewCycleCd ?? null }))
    .find((entry) => entry.cd !== null);
  const unmarkedStartDate = unmarkedStart
    ? addDays(parseISO(unmarkedStart.cycle.start_date), (unmarkedStart.cd as number) - 1)
    : null;
  const cycleRows = cycles.map((cycle, index) => {
    const timing = getCycleTiming(cycle, todayKey);
    const startDate = parseISO(cycle.start_date);
    // Past cycles run to their end. The current cycle runs through today, or through its last
    // logged day if entries were pre-logged past today (never past its own end). Upcoming
    // cycles run to their end, or their last logged day when open-ended.
    const lastLoggedKey = cycle.last_observation_date;
    const endKey =
      timing === "past"
        ? (cycle.end_date as string)
        : timing === "upcoming"
          ? cycle.end_date || lastLoggedKey
          : [cycle.end_date, lastLoggedKey > todayKey ? lastLoggedKey : todayKey]
              .filter((key): key is string => Boolean(key))
              .sort()[0];
    const endDate = parseISO(endKey < cycle.start_date ? cycle.start_date : endKey);
    const cycleDates = eachDayOfInterval({ start: startDate, end: endDate });

    return {
      id: cycle.id,
      label: cycle.start_inferred
        ? "Partial"
        : timing === "upcoming"
          ? "Upcoming"
          : timing === "current"
            ? "Current"
            : `Cycle ${index - upcomingCount + 1}`,
      startDate: cycle.start_date,
      endDate: cycle.end_date,
      firstPeakCd: cycle.first_peak_cd,
      phaseSpans: phaseSpansByCycleId.get(cycle.id),
      days: cycleDates.map((day, dayIndex) => {
        const dateKey = format(day, "yyyy-MM-dd");
        return {
          date: dateKey,
          cycleDay: dayIndex + 1,
          isToday: dateKey === todayKey,
          observation: cycleObservationsByDate.get(dateKey),
        };
      }),
    };
  });

  const renderCalendarBody = (isFullscreen = false) => (
    <>
      <div className="shrink-0 space-y-3 rounded-lg border bg-background/70 px-3 py-3">
        <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={handlePrevious}
                disabled={viewMode === "cycle"}
                aria-label="Previous month"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Previous month</TooltipContent>
          </Tooltip>

          <h2 className="w-full text-center text-xl font-bold">
            {viewMode === "month" ? format(currentMonth, "MMMM yyyy") : "Recent Cycles"}
          </h2>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleNext}
                disabled={viewMode === "cycle"}
                aria-label="Next month"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Next month</TooltipContent>
          </Tooltip>
        </div>

        <div className="flex justify-center">
          <div className="inline-flex rounded-lg bg-muted p-1 shadow-inner">
            <button
              className={`px-4 py-1 rounded-lg text-sm font-medium transition-colors duration-150 ${
                viewMode === "cycle"
                  ? "bg-primary text-primary-foreground shadow"
                  : "hover:bg-accent text-muted-foreground"
              }`}
              onClick={() => setViewMode("cycle")}
              aria-pressed={viewMode === "cycle"}
            >
              Cycle
            </button>
            <button
              className={`px-4 py-1 rounded-lg text-sm font-medium transition-colors duration-150 ${
                viewMode === "month"
                  ? "bg-primary text-primary-foreground shadow"
                  : "hover:bg-accent text-muted-foreground"
              }`}
              onClick={() => setViewMode("month")}
              aria-pressed={viewMode === "month"}
            >
              Month
            </button>
          </div>
        </div>

        <div className="border-t pt-3">
          <ObservationLegend
            bare
            visibleTypes={visibleIconTypes}
            onToggleType={toggleIconType}
            activeSymptomFilters={activeSymptomFilters}
            onToggleSymptomFilter={toggleSymptomFilter}
            visiblePhases={visiblePhases}
            onTogglePhase={togglePhase}
          />
        </div>
      </div>

      {viewMode === "cycle" ? (
        (() => {
          const cycleGrid = (
            <CycleView
              cycles={cycleRows}
              autoFocusCurrentDay
              showLegend={false}
              fillHeight={isFullscreen}
              className={isFullscreen ? "min-h-0 flex-1" : undefined}
              visibleTypes={visibleIconTypes}
              activeSymptomFilters={activeSymptomFilters}
              visiblePhases={visiblePhases}
              onDaySelect={(day) => {
                setSelectedDay((current) => {
                  const currentKey = current ? format(current, "yyyy-MM-dd") : null;
                  return currentKey === day.date ? null : parseISO(day.date);
                });
              }}
            />
          );

          return isFullscreen ? (
            cycleGrid
          ) : (
            <Card className="shadow-[var(--shadow-soft)] animate-fade-in p-4">
              {cycleGrid}
            </Card>
          );
        })()
      ) : (
        <Card
          className={cn(
            "shadow-[var(--shadow-soft)] animate-fade-in p-6",
            isFullscreen && "min-h-0 flex-1 overflow-y-auto"
          )}
        >
          <div className="grid grid-cols-7 gap-2">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
              <div
                key={day}
                className="p-2 text-center text-sm font-semibold text-muted-foreground"
              >
                {day}
              </div>
            ))}

            {Array.from({ length: monthStartOffset }).map((_, i) => (
              <div key={`empty-${i}`} />
            ))}

            {monthDays.map((day) => {
              const obs = getObservationForDay(day);
              const cycleStart = getCycleForDay(day);
              const isToday = isSameDay(day, new Date());
              const dayCycle = getCycleForDate(cycles, format(day, "yyyy-MM-dd"), todayKey);
              // Cycle day comes from the cycle the date falls in, not from the observation —
              // a day with nothing logged still sits inside a phase.
              const dayCycleDay = dayCycle
                ? differenceInCalendarDays(day, parseISO(dayCycle.start_date)) + 1
                : null;
              const dayPhaseSpans = dayCycle
                ? phaseSpansByCycleId.get(dayCycle.id) ?? []
                : [];
              // Greyed only inside the post-Peak phase (the method's own close, ending at an
              // unmarked next period) — not at a flat Peak + 3.
              const isPostPeakNonFertile =
                dayCycleDay !== null && isPostPeakPhaseDay(dayCycleDay, dayPhaseSpans);
              const dayPhases =
                dayCycleDay !== null
                  ? describeDayPhases(dayCycleDay, dayPhaseSpans, visiblePhases)
                  : [];

              return (
                <div
                  key={day.toISOString()}
                  onClick={() => setSelectedDay(day)}
                  aria-label={
                    dayPhases.length
                      ? `${format(day, "MMMM d")} — ${dayPhases.join(", ")}`
                      : undefined
                  }
                  className={`
                    relative min-h-[80px] cursor-pointer overflow-hidden rounded-lg border p-2 transition-colors
                    ${getObservationCellStyle(obs, { isPostPeakNonFertile })}
                    ${isToday ? "ring-2 ring-primary/60" : ""}
                    ${cycleStart ? "ring-2 ring-green-500 ring-offset-1" : ""}
                    ${getSymptomHighlightClass(obs, activeSymptomFilters)}
                    hover:border-primary
                  `}
                >
                  {dayCycleDay !== null && visiblePhases.length ? (
                    // Pulled out to the cell's edges (cancelling its p-2) so a run of days
                    // reads as one line broken only by the grid gap.
                    <div className="-mx-2 -mt-2 mb-1 flex h-2 flex-col justify-end">
                      <PhaseBandStack
                        cycleDay={dayCycleDay}
                        spans={dayPhaseSpans}
                        visiblePhases={visiblePhases}
                      />
                    </div>
                  ) : null}
                  <div className="flex items-start justify-between">
                    <div className="text-sm font-medium">{format(day, "d")}</div>
                    {cycleStart && (
                      <Badge
                        variant="default"
                        className="h-5 bg-green-600 px-1.5 py-0 text-xs hover:bg-green-600"
                      >
                        CD1
                      </Badge>
                    )}
                  </div>
                  {obs && (
                    <ObservationCellIcons
                      observation={obs}
                      size="lg"
                      visibleTypes={visibleIconTypes}
                      activeSymptomFilters={activeSymptomFilters}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </>
  );

  return (
    <SubscriptionGate
      coupleId={coupleId}
      readOnlyMessage="Subscribe to access your calendar view and track your cycles."
    >
      <div className="space-y-4 pb-8">
        {unmarkedStartDate && (
          <p className="rounded-lg border bg-background/70 px-4 py-3 text-sm text-muted-foreground">
            Looks like a new cycle started on {format(unmarkedStartDate, "MMM d")} — mark Cycle
            start on that day so the next cycle is charted separately.
          </p>
        )}
        {renderCalendarBody()}

        <Dialog open={!!selectedDay} onOpenChange={() => setSelectedDay(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {selectedDay && format(selectedDay, "MMMM d, yyyy")}
              </DialogTitle>
              <DialogDescription>
                Detailed fertility observations recorded for this date.
              </DialogDescription>
            </DialogHeader>
            {selectedObs ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Cycle Day</p>
                    <p className="text-lg font-semibold">
                      {selectedObs.cycle_start_inferred ? "Not recorded" : selectedObs.cycle_day}
                    </p>
                  </div>
                  {selectedObs.temperature_f && (
                    <div>
                      <p className="text-sm text-muted-foreground">Temperature</p>
                      <p className="text-lg font-semibold">
                        {formatTemperature(selectedObs.temperature_f, "°F")}
                      </p>
                    </div>
                  )}
                  {selectedObs.bleeding && (
                    <div>
                      <p className="text-sm text-muted-foreground">Bleeding</p>
                      <p className="text-lg font-semibold">{selectedObs.bleeding}</p>
                    </div>
                  )}
                  {selectedObs.monitor && (
                    <div>
                      <p className="text-sm text-muted-foreground">Monitor</p>
                      <Badge variant="secondary">{selectedObs.monitor}</Badge>
                    </div>
                  )}
                  {(selectedObs.ovulation_test === "positive" ||
                    selectedObs.ovulation_test === "negative") && (
                    <div>
                      <p className="text-sm text-muted-foreground">Ovulation test</p>
                      <Badge variant={selectedObs.ovulation_test === "positive" ? "default" : "secondary"}>
                        {selectedObs.ovulation_test === "positive" ? "Positive" : "Negative"}
                      </Badge>
                    </div>
                  )}
                  {selectedObs.mucus && (
                    <div>
                      <p className="text-sm text-muted-foreground">Mucus</p>
                      <p className="text-sm">
                        {selectedObs.mucus}
                        {selectedObs.mucus_frequency ? ` (${selectedObs.mucus_frequency})` : ""}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-muted-foreground">Intercourse</p>
                    <p className="text-lg">{selectedObs.intercourse ? "Yes" : "No"}</p>
                  </div>
                </div>
                {selectedObs.symptoms && selectedObs.symptoms.length > 0 && (
                  <div>
                    <p className="mb-2 text-sm text-muted-foreground">Symptoms</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedObs.symptoms.map((symptom) => (
                        <Badge key={symptom} variant="outline">
                          {symptom}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {selectedObs.notes && (
                  <div>
                    <p className="mb-2 text-sm text-muted-foreground">Notes</p>
                    <p className="rounded-md bg-muted p-3 text-sm">{selectedObs.notes}</p>
                  </div>
                )}
                {!isClientView && (
                  <Button
                    onClick={() => {
                      navigate(`${ROUTES.observations}?date=${selectedObs.date}`);
                    }}
                    className="w-full"
                  >
                    Edit Observation
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-muted-foreground">
                  No observation recorded for this day.
                </p>
                {!isClientView && (
                  <Button
                    onClick={() => {
                      navigate(
                        `${ROUTES.observations}?date=${format(selectedDay!, "yyyy-MM-dd")}`
                      );
                    }}
                    className="w-full"
                  >
                    Add Observation
                  </Button>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        <div className="flex justify-start pt-2">
          <Button variant="outline" size="sm" onClick={handleExportPDF}>
            <FileText className="mr-2 h-4 w-4" />
            Export PDF
          </Button>
        </div>

        {!isClientView && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                aria-label="Log new entry"
                className="safe-fixed-bottom fixed right-[calc(env(safe-area-inset-right,0px)+1.5rem)] z-50 flex h-14 w-14 items-center justify-center rounded-full bg-primary shadow-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                onClick={() => navigate(`${ROUTES.observations}?date=${todayKey}`)}
              >
                <span className="sr-only">Log new entry</span>
                <svg
                  width="28"
                  height="28"
                  fill="none"
                  stroke="white"
                  strokeWidth="3"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                </svg>
              </button>
            </TooltipTrigger>
            <TooltipContent side="left">Log entry</TooltipContent>
          </Tooltip>
        )}

        <Dialog open={isFullscreenOpen} onOpenChange={setIsFullscreenOpen}>
          <DialogContent className="flex h-[92vh] w-[96vw] max-w-none flex-col overflow-hidden sm:rounded-lg">
            <DialogTitle className="sr-only">
              {viewMode === "month" ? format(currentMonth, "MMMM yyyy") : "Recent Cycles"}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {isClientView ? "Full-screen view of this client's cycle and symptom calendar." : "Full-screen view of your cycle and symptom calendar."}
            </DialogDescription>
            <div className="flex min-h-0 flex-1 flex-col gap-4">
              {renderCalendarBody(true)}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </SubscriptionGate>
  );
};

export default Calendar;
